import { getFastTrackerReporter } from "./fastTracker";

export const hongKongSectionDurationEvent = "hk_home_section_view_duration";
export const hongKongClickEvent = "hk_home_click";
export const hongKongFanHoverEvent = "hk_home_fan_item_hover";

type FlushReason = "section_change" | "pagehide" | "route_leave";
type Reporter = ReturnType<typeof getFastTrackerReporter>;
type DebugWindow = Window & {
  __claweaseAnalyticsDebugEvents__?: Array<{
    event: string;
    module: string;
    urgent: boolean;
    payload: Record<string, unknown>;
  }>;
};
const debugElementId = "ce-hk-analytics-debug";

const sectionNames: Record<string, string> = {
  hero: "hero",
  how: "benefits",
  why: "why_clawease",
  pricing: "pricing",
  faq: "faq",
};

function interactionId(element: HTMLElement) {
  if (element.dataset.analyticsId) return element.dataset.analyticsId;
  if (element.dataset.homeFeature) return `feature_${element.dataset.homeFeature}`;
  if (element.dataset.homeOption) return `option_${element.dataset.homeOption}`;
  if (element.id) return element.id;
  const controls = element.getAttribute("aria-controls");
  if (controls) return `control_${controls}`;
  if (element instanceof HTMLAnchorElement) {
    try {
      const url = new URL(element.href, window.location.origin);
      const source = url.searchParams.get("source") || url.searchParams.get("entry_source");
      return source ? `link_${source}` : `link_${url.pathname}${url.hash}`;
    } catch {
      return "link";
    }
  }
  if (element instanceof HTMLInputElement) return `input_${element.name || element.type}`;
  return element.getAttribute("aria-label") || element.tagName.toLowerCase();
}

/**
 * Hong Kong home-page analytics. It uses the same FastTracker reporter and
 * duration payload shape as the dental landing page. Pointer hover events are
 * emitted once per real entry, rather than on every child-level pointer move.
 */
export function installHongKongHomeAnalytics(root: HTMLElement, reportOverride?: Reporter) {
  const document = root.ownerDocument;
  const browser = document.defaultView;
  if (!browser) return () => {};
  if (import.meta.env.DEV) {
    (browser as DebugWindow).__claweaseAnalyticsDebugEvents__ = [];
    let debugElement = document.getElementById(debugElementId);
    if (!debugElement) {
      debugElement = document.createElement("script");
      debugElement.id = debugElementId;
      debugElement.setAttribute("type", "application/json");
      document.head.appendChild(debugElement);
    }
    debugElement.textContent = "[]";
  }
  const report = reportOverride ?? getFastTrackerReporter(browser);
  const main = root.querySelector<HTMLElement>("main");
  if (!main) return () => {};
  const sections = Array.from(main.querySelectorAll<HTMLElement>(":scope > section[id]"))
    .filter((element) => element.id in sectionNames)
    .map((element, index) => ({ id: element.id, name: sectionNames[element.id], index: index + 1, element }));
  if (!sections.length) return () => {};

  const pageViewId = browser.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const pagePath = browser.location.pathname;
  const totals = new Map<string, number>();
  let sequence = 0;
  let active: (typeof sections)[number] | null = null;
  let lastAt: number | null = null;
  let pendingDuration = 0;
  let frame = 0;
  let suspended = false;
  let disposed = false;
  const now = () => Math.floor(browser.performance.now());
  const visible = () => !disposed && !suspended && document.visibilityState === "visible" && root.isConnected;
  const nextEvent = () => `${pageViewId}:${++sequence}`;

  const safeReport = (event: string, payload: Record<string, unknown>, urgent = false) => {
    const eventPayload = {
      schema_version: 1,
      page_path: pagePath,
      page_view_id: pageViewId,
      event_id: nextEvent(),
      sequence,
      ...payload,
    };
    if (import.meta.env.DEV) {
      const debugWindow = browser as DebugWindow;
      (debugWindow.__claweaseAnalyticsDebugEvents__ ??= []).push({
        event,
        module: "hk_home",
        urgent,
        payload: eventPayload,
      });
      document.getElementById(debugElementId)!.textContent = JSON.stringify(debugWindow.__claweaseAnalyticsDebugEvents__);
    }
    try {
      report(event, eventPayload, "hk_home", urgent);
    } catch {
      // Analytics must never affect page interaction or navigation.
    }
  };

  const accrue = () => {
    const time = now();
    if (active && lastAt !== null) pendingDuration += Math.max(0, time - lastAt);
    if (lastAt !== null) lastAt = time;
  };

  const flush = (reason: FlushReason) => {
    accrue();
    const duration = pendingDuration;
    pendingDuration = 0;
    if (!active || duration <= 0) return;
    const total = (totals.get(active.id) ?? 0) + duration;
    totals.set(active.id, total);
    safeReport(hongKongSectionDurationEvent, {
      section_id: active.id,
      section_name: active.name,
      section_index: active.index,
      duration_ms: duration,
      section_total_ms: total,
      reason,
    }, reason !== "section_change");
  };

  const update = () => {
    if (!visible()) return;
    const readingLine = browser.scrollY + Math.min(browser.innerHeight * 0.42, 420);
    const bounds = sections.map(({ element }) => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top + browser.scrollY, bottom: rect.bottom + browser.scrollY };
    });
    let activeIndex = bounds.findIndex(({ top, bottom }) => readingLine >= top && readingLine < bottom);
    if (activeIndex < 0) {
      activeIndex = bounds.findIndex(({ top, bottom }) => bottom > browser.scrollY && top < browser.scrollY + browser.innerHeight);
    }
    const next = activeIndex >= 0 ? sections[activeIndex] : null;
    if (active !== next) {
      flush("section_change");
      active = next;
    }
    lastAt = active ? lastAt ?? now() : null;
  };

  const schedule = () => {
    if (visible() && !frame) frame = browser.requestAnimationFrame(() => { frame = 0; update(); });
  };
  const pause = () => {
    accrue();
    lastAt = null;
    browser.cancelAnimationFrame(frame);
    frame = 0;
  };
  const start = () => { if (visible()) update(); };
  const onVisibility = () => document.visibilityState === "visible" ? start() : pause();
  const onPageHide = () => { suspended = true; pause(); flush("pagehide"); };
  const onPageShow = () => { suspended = false; start(); };

  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>("a,button,summary,input,select")
      : null;
    if (!target || !root.contains(target)) return;
    const section = target.closest<HTMLElement>("section[id]");
    const scope = section || target.closest<HTMLElement>("header") || root;
    const interactive = Array.from(scope.querySelectorAll<HTMLElement>("a,button,summary,input,select"));
    safeReport(hongKongClickEvent, {
      section_id: section?.id || (target.closest("header") ? "navigation" : "page"),
      element_id: interactionId(target),
      element_type: target.tagName.toLowerCase(),
      element_index: interactive.indexOf(target) + 1,
      feature_id: target.dataset.homeFeature || "",
      option_id: target.dataset.homeOption || "",
      expanded: target.getAttribute("aria-expanded") || "",
      selected: target.getAttribute("aria-selected") || target.getAttribute("aria-pressed") || "",
    });
  };

  const onPointerOver = (event: PointerEvent) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>("[data-home-feature],[data-home-option]")
      : null;
    if (!target || !root.contains(target)) return;
    if (event.relatedTarget instanceof Node && target.contains(event.relatedTarget)) return;
    const featureId = target.dataset.homeFeature;
    const optionId = target.dataset.homeOption;
    safeReport(hongKongFanHoverEvent, {
      section_id: "hero",
      item_kind: featureId ? "large" : "small",
      item_id: featureId || optionId || "",
    });
  };

  start();
  browser.addEventListener("scroll", schedule, { passive: true });
  browser.addEventListener("resize", schedule);
  browser.addEventListener("pagehide", onPageHide);
  browser.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibility);
  root.addEventListener("click", onClick);
  root.addEventListener("pointerover", onPointerOver);
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
  observer?.observe(main);
  sections.forEach(({ element }) => observer?.observe(element));

  return () => {
    if (disposed) return;
    pause();
    flush("route_leave");
    disposed = true;
    browser.removeEventListener("scroll", schedule);
    browser.removeEventListener("resize", schedule);
    browser.removeEventListener("pagehide", onPageHide);
    browser.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibility);
    root.removeEventListener("click", onClick);
    root.removeEventListener("pointerover", onPointerOver);
    observer?.disconnect();
    if (import.meta.env.DEV) document.getElementById(debugElementId)?.remove();
  };
}
